// M6 — Auth surface. Backed by labourcore-api instead of Supabase Auth.
// Preserves the same export shape as the v49.0 module so callers need no changes.

import { supabase, SUPABASE_READY, friendlyErrorMessage } from "./supabase-client";
import { lcAudit } from "../audit";

// Compat types — these replace the @supabase/supabase-js imports
export interface Session {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    user_metadata: Record<string, unknown>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface AuthSubscription {
  unsubscribe: () => void;
}

export const auth = {
  async getSession(): Promise<Session | null> {
    if (!SUPABASE_READY) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  },
  async signUp(
    email: string,
    password: string,
    metadata?: Record<string, unknown>,
  ) {
    if (!SUPABASE_READY) throw new Error("API not configured");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
    lcAudit({ event_type: "auth.signed_up", ui_string: email, metadata: { has_metadata: !!metadata } });
    return data;
  },
  async signIn(email: string, password: string) {
    if (!SUPABASE_READY) throw new Error("API not configured");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    lcAudit({ event_type: "auth.signed_in", ui_string: email });
    return data;
  },
  async signOut() {
    if (!SUPABASE_READY) return;
    lcAudit({ event_type: "auth.signed_out" });
    await supabase.auth.signOut();
  },
  /** Request a forgot-password email (always generic success on the API). */
  async requestPasswordReset(email: string) {
    const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
    const res = await fetch(`${BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new Error(friendlyErrorMessage(json, `HTTP ${res.status}`));
    }
    lcAudit({ event_type: "auth.forgot_password_requested", ui_string: email });
    return { ok: true as const };
  },
  /** Complete password reset with Bearer reset_token; returns session tokens. */
  async resetPasswordWithToken(resetToken: string, newPassword: string) {
    const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
    const res = await fetch(`${BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${resetToken}`,
      },
      body: JSON.stringify({ new_password: newPassword }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(friendlyErrorMessage(json, `HTTP ${res.status}`));
    lcAudit({ event_type: "auth.password_reset", ui_string: json.user?.email });
    return json as {
      user: { id: string; email: string };
      access_token: string;
      refresh_token: string;
    };
  },
  onAuthStateChange(handler: (event: string, session: Session | null) => void): AuthSubscription | { unsubscribe: () => void } {
    if (!SUPABASE_READY) return { unsubscribe: () => {} };
    const { data } = supabase.auth.onAuthStateChange(handler as any);
    return data?.subscription || { unsubscribe: () => {} };
  },

  /* MFA stubs — not implemented in labourcore-api */
  mfa: {
    async listFactors() {
      if (!SUPABASE_READY) return { totp: [], phone: [] };
      return { totp: [], phone: [] };
    },
    async getAal() {
      if (!SUPABASE_READY) return { currentLevel: null, nextLevel: null };
      return { currentLevel: null, nextLevel: null };
    },
    async enroll(_friendlyName: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async challenge(_factorId: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async verify(_args: { factorId: string; challengeId: string; code: string }) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async challengeAndVerify(_args: { factorId: string; code: string }) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async unenroll(_factorId: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
  },
};
